import { useTranslation } from "react-i18next";
import { Skeleton } from "@/components/ui";
import { type OccurrencePreviewResult } from "@/hooks/use-occurrence-preview";
import { parseLocalDate } from "./recurrence-rrule";

const PREVIEW = "schedules.wizard.preview";

export interface OccurrencePreviewProps {
  /** Output of `useOccurrencePreview` for the rule being built. */
  preview: OccurrencePreviewResult;
  /** Wizard-level start date; empty string renders the pick-a-date state. */
  startDate: string;
  /** Generation horizon driving the "…and continues" line. */
  horizonDays: number;
}

/**
 * Presentational next-occurrences panel — the operator's trust mechanism:
 * concrete server-computed dates, not rule syntax. Shared by the wizard's
 * recurrence step, the review step (same cached query), and the edit page.
 * State precedence: no start date → engine 422 (verbatim message) → network
 * failure + Retry → date rows → first-load skeleton → zero-occurrence warning.
 */
export function OccurrencePreview({ preview, startDate, horizonDays }: OccurrencePreviewProps) {
  const { t, i18n } = useTranslation(["test-cadence"]);
  const locale = i18n.language;

  function renderBody() {
    if (startDate === "") {
      return (
        <p className="px-4 py-6 text-center text-sm text-muted">{t(`${PREVIEW}.pickStartDate`)}</p>
      );
    }
    if (preview.invalidRuleMessage !== null) {
      return (
        <div role="alert" className="m-3 rounded-md bg-danger/10 p-3 text-sm text-danger">
          {preview.invalidRuleMessage}
        </div>
      );
    }
    if (preview.isError) {
      return (
        <div className="px-4 py-6 text-center text-sm text-muted">
          <p>{t(`${PREVIEW}.networkError`)}</p>
          <button
            type="button"
            onClick={preview.refetch}
            className="mt-1 font-medium text-primary hover:underline"
          >
            {t(`${PREVIEW}.retry`)}
          </button>
        </div>
      );
    }
    if (preview.occurrences.length > 0) {
      const weekdayFormat = new Intl.DateTimeFormat(locale, { weekday: "short" });
      const dateFormat = new Intl.DateTimeFormat(locale, { dateStyle: "long" });
      return (
        <ul>
          {preview.occurrences.map((occurrence) => (
            <li
              key={occurrence}
              className="flex gap-3 border-b border-gray-100 px-4 py-2 text-sm last:border-b-0 dark:border-gray-700"
            >
              <span className="w-10 flex-none text-muted">
                {weekdayFormat.format(parseLocalDate(occurrence))}
              </span>
              <span className="text-on-surface">
                {dateFormat.format(parseLocalDate(occurrence))}
              </span>
            </li>
          ))}
          {(preview.hasMore || preview.openEnded) && (
            <li className="px-4 py-2 text-sm italic text-muted">
              {t(`${PREVIEW}.andContinues`, { horizonDays })}
            </li>
          )}
        </ul>
      );
    }
    if (preview.isPending || preview.isDebouncing) {
      return (
        <div className="space-y-2.5 px-4 py-3">
          <Skeleton width="60%" />
          <Skeleton width="55%" />
          <Skeleton width="58%" />
        </div>
      );
    }
    return (
      <div role="alert" className="m-3 rounded-md bg-warning/10 p-3 text-sm text-on-surface">
        {t(`${PREVIEW}.zeroOccurrences`)}
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between border-b border-gray-200 bg-gray-50 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900/40">
        <span className="text-sm font-semibold text-on-surface">{t(`${PREVIEW}.title`)}</span>
        <span className="text-xs text-muted">{t(`${PREVIEW}.livePreview`)}</span>
      </div>
      {renderBody()}
    </div>
  );
}
